import * as React from 'react';
import { Flex } from 'grid-styled';
import { pure } from 'recompose';

/* UI */

/* Utils */
import { removeNonNumericCharacters } from '../../../../utils/formattingHelpers';
import { getSanitizedProperty } from '../../../../utils/validation/date';

/* Styles */
import './Date.scss';
import { getPresetInputBox } from '../../../../styles/input.style';

/* Types */
import { DateEnum } from './types';
import {
  CustomKeyboardEvent,
  DateInputProps,
  EventTargetWithSelection,
} from '../types';

interface CustomDateChangeEvent extends React.ChangeEvent<HTMLInputElement> {
  target: EventTargetWithSelection & HTMLInputElement;
}

interface DateInputState {
  focused: boolean;
  dummyFocused: boolean;
}

class DateInput extends React.Component<DateInputProps, DateInputState> {
  constructor(props: DateInputProps) {
    super(props);
    this.state = {
      focused: false,
      dummyFocused: false,
    };
  }

  monthRef = null as any;
  dayRef = null as any;
  yearRef = null as any;

  dayOnBlur = (): void => this.onPropertyBlur('day');
  dayOnChange = (event: CustomDateChangeEvent): void =>
    this.onPropertyChange(event, 'day');
  dayOnKeyDown = (event: CustomKeyboardEvent): void =>
    this.onKeyDown(event, 'day');
  monthOnBlur = (): void => this.onPropertyBlur('month');
  monthOnChange = (event: CustomDateChangeEvent): void =>
    this.onPropertyChange(event, 'month');
  monthOnKeyDown = (event: CustomKeyboardEvent): void =>
    this.onKeyDown(event, 'month');
  yearOnBlur = (): void => this.onPropertyBlur('year');
  yearOnChange = (event: CustomDateChangeEvent): void =>
    this.onPropertyChange(event, 'year');
  yearOnKeyDown = (event: CustomKeyboardEvent): void =>
    this.onKeyDown(event, 'year');

  onKeyDown = (event: CustomKeyboardEvent, propertyName: DateEnum): void => {
    const keyValue = String.fromCharCode(event.keyCode);
    const key = event.key === 'Unidentified' ? keyValue : event.key;

    if (key === 'Enter') {
      return this.props.onKeyDown(event);
    }

    const propertyVal = this.props.input[propertyName];

    // If the cursor is at the front of the field, we can go to previous field from year and day.
    if (
      event.target.selectionStart === 0 &&
      (propertyName === 'year' || propertyName === 'day')
    ) {
      if (key === 'ArrowLeft') {
        this.switchFocusBackward(propertyName, false);
      }
      // If nothing is selected (selectionStart & selectionEnd are both 0),
      // Backspace into the previous field.
      if (event.target.selectionEnd === 0 && key === 'Backspace') {
        this.switchFocusBackward(propertyName, true);
      }
    }

    // Allow arrow right to switch to next field if the cursor is at end of input.
    if (
      (!propertyVal || propertyVal.length === event.target.selectionStart) &&
      (propertyName === 'month' || propertyName === 'day')
    ) {
      if (key === 'ArrowRight') {
        this.switchFocusForward(propertyName, true);
      }
    }
  };

  onChange = (
    propertyName: DateEnum,
    value: string,
    onSetStateCompleted?: () => void,
  ) => {
    // Get the full date using the new value for propertyName.
    const input = { ...this.props.input };
    input[propertyName] = value;
    // Send it up in the standard format: id, inputValue, optional callback.
    this.props.onChange(this.props.id, input, onSetStateCompleted);
  };

  onPropertyChange = (
    event: CustomDateChangeEvent,
    propertyName: DateEnum,
  ): void => {
    const { value } = event.target;
    // Safety check: Limit number of characters per field; MM DD YYYY.
    if (
      (propertyName === 'month' || propertyName === 'day') &&
      value.length > 2
    ) {
      return undefined;
    }
    if (propertyName === 'year' && value.length > 4) return undefined;

    // Allow text update.
    const numberOnlyText = removeNonNumericCharacters(value);
    let onSetStateCompleted;
    if (
      (propertyName === 'month' || propertyName === 'day') &&
      numberOnlyText.length === 2
    ) {
      // We have reached the end of this input; auto switch to the next field after the input has been updated.
      // We must wait until the setState propogates, otherwise onBlur will fire too early and not get the character.

      onSetStateCompleted = () => {
        // this.switchFocusForward(propertyName, false);
        // TODO(gemma): There has GOT to be a way to do this w/out using setTimeout...
        setTimeout(() => this.switchFocusForward(propertyName, false), 0);
      };
    }
    this.onChange(propertyName, numberOnlyText, onSetStateCompleted);
  };

  onPropertyBlur = (propertyName: DateEnum): void => {
    // Reformat input if needed and trigger change.
    if (this.props.input[propertyName]) {
      let sanitizedProp = getSanitizedProperty(
        propertyName,
        this.props.input[propertyName],
      );
      // Halt the blur until we format the input. Blur could show an error prematurely.
      const onSetStateCompleted = () => this.onBlur();
      this.onChange(propertyName, sanitizedProp, onSetStateCompleted);
    } else {
      this.onBlur();
    }
  };

  onBlur = (): void => {
    // If we blurred out completely from the date input, then trigger onBlur.
    // Unfortunately, activeElement isn't updated until after the blur event is processed, so
    // we have to be hacky and set timeout...
    setTimeout(() => {
      if (!this.isDateInputFocused()) {
        this.setState({ focused: false });
        this.props.onBlur(this.props.id);
      }
    }, 0);
  };

  isDateInputFocused = (): boolean => {
    // Check if we are focused on any of the date input fields.
    let activeEl = document.activeElement as HTMLInputElement;
    return [this.monthRef, this.dayRef, this.yearRef].indexOf(activeEl) !== -1;
  };

  onFocus = () => {
    this.setState({ dummyFocused: false, focused: true });
  };

  onDummyFocus = () => {
    this.setState({ dummyFocused: true, focused: true });
  };

  componentDidUpdate() {
    // The user focused on the empty input, so now focus the month input.
    if (this.state.dummyFocused) {
      this.monthRef.focus();
    }
  }

  removeLastCharacter = (currentValue: string): string => {
    return currentValue.substring(0, currentValue.length - 1);
  };

  switchFocusBackward = (field: string, backspace: boolean): any => {
    // Get previous field's property name.
    const focusDictionary = {
      day: 'month',
      year: 'day',
    };
    const affectedField = focusDictionary[field];
    const currentValue = this.props.input[affectedField];
    // Get previous field element.
    const ele = this.getRef(focusDictionary[field]) as HTMLInputElement;

    if (backspace) {
      const stringWithAppliedBackspace = this.removeLastCharacter(currentValue);
      this.onChange(affectedField, stringWithAppliedBackspace);
    }

    // Wait to focus on the new field, to prevent a backspace from deleting the whole field.
    // Otherwise the local onChange event fires on the new field
    // and doesn't have the updated value yet.
    return setTimeout(() => {
      if (!ele) return;
      ele.focus();
      setTimeout(() => ele.setSelectionRange(2, 2), 0);
    }, 0);
  };

  switchFocusForward = (field: string, rightArrow: boolean): void => {
    const focusDictionary = {
      month: 'day',
      day: 'year',
    };
    const ele = this.getRef(focusDictionary[field]) as HTMLInputElement;
    if (!ele) return;
    ele.focus();
    if (rightArrow) {
      // $FlowIssue property 'setSelectionRange' Property not found in HTMLElement
      setTimeout(() => ele.setSelectionRange(0, 0), 0);
    }
    return undefined;
  };

  getRef = (propertyName: string): any => {
    const refDictionary = {
      month: this.monthRef,
      day: this.dayRef,
      year: this.yearRef,
    };
    return refDictionary[propertyName];
  };

  getBoxStyle = () => {
    const { presetBoxStyle, inputBoxStyle, showError } = this.props;
    const { focused } = this.state;

    // If no preset is provided, we get a copy of the default styles.
    let boxStyles = getPresetInputBox(presetBoxStyle);

    let boxStyle;
    if (focused) {
      boxStyle = boxStyles.focused;
    } else if (showError) {
      boxStyle = boxStyles.error;
    } else {
      boxStyle = boxStyles.default;
    }

    // Overwrite 'style' with anything custom that effects all types; focused, error, and default.
    if (inputBoxStyle) {
      boxStyle.style = { ...boxStyle.style, ...inputBoxStyle };
    }

    return boxStyle;
  };

  getDateInput = (show: boolean) => {
    const {
      name,
      input: { day, month, year },
    } = this.props;

    // We need both dummy and input to be in the dom, but we toggle their visibility.
    let flexStyle = {
      height: '100%',
    } as React.CSSProperties;
    if (!show) {
      flexStyle.display = 'none';
    }

    let boxStyle = this.getBoxStyle();

    return (
      <Flex align="center" justify="center" style={flexStyle}>
        <input
          ref={ref => (this.monthRef = ref)}
          name={name}
          type="tel"
          value={month}
          placeholder={'MM'}
          className="date-month"
          data-cypress="date-month"
          onFocus={this.onFocus}
          onBlur={this.monthOnBlur}
          onChange={this.monthOnChange}
          onKeyDown={this.monthOnKeyDown}
          style={boxStyle.inputStyle}
        />
        <div className="date-slash">/</div>
        <input
          ref={ref => (this.dayRef = ref)}
          name={name}
          type="tel"
          value={day}
          placeholder={'DD'}
          className="date-day"
          data-cypress="date-day"
          onFocus={this.onFocus}
          onBlur={this.dayOnBlur}
          onChange={this.dayOnChange}
          onKeyDown={this.dayOnKeyDown}
          style={boxStyle.inputStyle}
        />
        <div className="date-slash">/</div>
        <input
          ref={ref => (this.yearRef = ref)}
          name={name}
          type="tel"
          value={year}
          placeholder={'YYYY'}
          min={1900}
          className="date-year"
          data-cypress="date-year"
          onFocus={this.onFocus}
          onBlur={this.yearOnBlur}
          onChange={this.yearOnChange}
          onKeyDown={this.yearOnKeyDown}
          style={boxStyle.inputStyle}
        />
      </Flex>
    );
  };

  getDummyInput = (show: boolean) => {
    // The dummy input is for allowing someone to focus when the label is front and center.
    // We need both dummy and input to be in the dom, but we toggle their visibility.
    let flexStyle = { height: '100%' } as React.CSSProperties;
    if (!show) {
      flexStyle.display = 'none';
    }

    let boxStyle = this.getBoxStyle();

    return (
      <Flex align="center" justify="center" style={flexStyle}>
        <input
          id="dummy"
          className="date-dummy"
          style={boxStyle.inputStyle}
          onFocus={this.onDummyFocus}
          value=""
          readOnly
        />
      </Flex>
    );
  };

  render() {
    const {
      floatingLabelText,
      showError,
      id,
      input: { day, month, year },
    } = this.props;
    const { focused } = this.state;

    const boxStyle = this.getBoxStyle();
    const isEmpty = !day && !month && !year;

    // Classes mimicking material UI built-in behaviors.
    let labelClass = 'date-transition-label ';
    labelClass += focused ? 'active ' : 'inactive ';
    labelClass += isEmpty ? 'empty ' : '';
    labelClass += focused || !isEmpty ? 'shrink ' : '';

    if (floatingLabelText && !boxStyle.style['width']) {
      console.warn(
        'Using the dummy input without a set width. It will probably jump around',
      );
    }

    const showDummy = !!(floatingLabelText && isEmpty && !focused);

    return (
      <div
        id={id}
        className={'date-inputContainer' + (showError ? ' error' : '')}
        style={boxStyle.style}
      >
        {floatingLabelText && (
          <label className={labelClass}>{floatingLabelText}</label>
        )}
        {this.getDummyInput(showDummy)}
        {this.getDateInput(!showDummy)}
      </div>
    );
  }
}

export default pure(DateInput);
